export type WebLogLevel = 'info' | 'warn' | 'error';

export interface WebLogAttributes {
  url?: string;
  name?: string;
  stack?: string;
  source?: string;
  lineno?: number;
  colno?: number;
  [key: string]: unknown;
}

export interface WebLogPayload {
  level: WebLogLevel;
  message: string;
  attributes?: WebLogAttributes;
}

/**
 * Strip query string and hash from a URL so credential tokens carried in query
 * params (auth flows on this app use ?token=…) never reach the remote log store.
 */
function sanitizeUrl(raw: string): string {
  try {
    const u = new URL(raw, window.location.origin);
    return `${u.origin}${u.pathname}`;
  } catch {
    return raw.split('?')[0].split('#')[0];
  }
}

// Sliding-window rate limiter: at most MAX_POSTS_PER_WINDOW POSTs per WINDOW_MS.
const WINDOW_MS = 10_000;
const MAX_POSTS_PER_WINDOW = 30;
const recentTimestamps: number[] = [];

// Browser/axios "network request failed" messages. These are indistinguishable
// from a genuine backend outage by message alone, and on this app overwhelmingly
// come from in-flight requests aborted by page navigation, offline blips, or
// adblockers — not actionable bugs. Backend outages are surfaced to users via
// toasts and monitored server-side, so dropping these client logs loses nothing.
const NETWORK_BLIP_MESSAGES = new Set([
  'Load failed', // WebKit / Safari
  'Failed to fetch', // Chrome / Blink
  'NetworkError when attempting to fetch resource.', // Firefox / Gecko (trailing period intentional)
  'Network Error', // axios
]);

// Non-actionable errors injected by browser extensions / email-client link
// scanners (e.g. Outlook Safe Links), not originating from app code. The Id /
// MethodName / ParamCount values vary per occurrence, so match the shape rather
// than an exact string. Anchored so a genuine app error that merely mentions the
// phrase still forwards.
const NOISE_MESSAGE_PATTERNS: readonly RegExp[] = [
  /^Object Not Found Matching Id:\d+, MethodName:\w+, ParamCount:\d+$/,
];

// URL schemes of browser-extension content scripts. Errors thrown by injected
// extension code (e.g. MetaMask's inpage.js "Failed to connect to MetaMask",
// wallet/dapp probes, ad-blockers) are not actionable app bugs — the extension
// runs on every page regardless of our code.
const EXTENSION_URL_SCHEMES: readonly string[] = [
  'chrome-extension://',
  'moz-extension://',
  'safari-web-extension://',
  'safari-extension://',
];

function hasExtensionScheme(value: string): boolean {
  return EXTENSION_URL_SCHEMES.some((scheme) => value.includes(scheme));
}

/**
 * The top (throw-site) frame of a stack string, or undefined if none is found.
 *
 * Matches an actual frame line — V8 (`    at fn (url)`) or Firefox/Safari
 * (`fn@url` / `@url`) — rather than "the first line containing a URL". This
 * deliberately skips V8's leading `Name: message` header: a header that happens
 * to contain a URL (even an extension one) must not be mistaken for the origin,
 * and the true top frame must be found even when the header carries an unrelated
 * URL. Only the first frame is returned, so a deeper extension frame under an
 * app/anonymous top frame is never treated as the origin.
 */
function topStackFrame(stack: string): string | undefined {
  return stack.split('\n').find((line) => /^\s*at\s/.test(line) || /^\s*\S*@\S/.test(line));
}

/**
 * True when the error *originated* in browser-extension code, judged by its
 * origin only — the captured `source` (script URL of a global `error` event) or
 * the **top** stack frame. Deeper extension frames are ignored on purpose: a
 * genuine app error whose stack merely passes through an extension wrapper still
 * forwards. This drop lives in the forwarder (not the global handlers) for the
 * same two-path reason as the message filters above — extension noise reaches
 * here both uncaught (`unhandledrejection`, e.g. MetaMask's async connect) and
 * caught-then-relogged.
 */
function isExtensionOriginError(attributes?: WebLogAttributes): boolean {
  if (!attributes) {
    return false;
  }

  const { source, stack } = attributes;
  if (typeof source === 'string' && EXTENSION_URL_SCHEMES.some((scheme) => source.startsWith(scheme))) {
    return true;
  }

  if (typeof stack === 'string') {
    const topFrame = topStackFrame(stack);
    if (topFrame !== undefined && hasExtensionScheme(topFrame)) {
      return true;
    }
  }

  return false;
}

/**
 * Fire-and-forget POST to the auth-api client-log endpoint.
 * Skipped entirely in local development (REACT_APP_ENV === 'development').
 * Rate-limited to MAX_POSTS_PER_WINDOW calls per WINDOW_MS to prevent error/render
 * loops from flooding the server.
 */
export function webLogPost({ level, message, attributes }: WebLogPayload): void {
  if (process.env.REACT_APP_ENV === 'development') {
    return;
  }

  // Drop non-actionable noise (network blips, injected extension/link-scanner
  // errors) before it consumes rate-limit budget.
  if (NETWORK_BLIP_MESSAGES.has(message) || NOISE_MESSAGE_PATTERNS.some((re) => re.test(message))) {
    return;
  }

  // Drop errors that originated in browser-extension code (matched by origin,
  // not message, since these carry app-shaped messages like "Failed to connect
  // to MetaMask").
  if (isExtensionOriginError(attributes)) {
    return;
  }

  // Read process.env directly (not getAuthApiUrl from app-config) to avoid a circular import.
  const rawHost = (process.env.REACT_APP_MAIN_URL ?? '').trim();
  if (!rawHost) {
    return;
  }

  // Sliding-window gate: drop stale timestamps, then check the count.
  const now = Date.now();
  const cutoff = now - WINDOW_MS;
  while (recentTimestamps.length > 0 && (recentTimestamps[0] as number) <= cutoff) {
    recentTimestamps.shift();
  }
  if (recentTimestamps.length >= MAX_POSTS_PER_WINDOW) {
    return;
  }
  recentTimestamps.push(now);

  const host = rawHost.replace(/\/$/, '');
  const url = `${host}/api/v1/public/client-log`;
  const body = {
    attributes: {
      // Caller attributes spread first so the captured url always wins.
      ...attributes,
      url: sanitizeUrl(window.location.href),
    },
    level,
    message,
    service: 'pp-gateway',
  };

  void fetch(url, {
    body: JSON.stringify(body),
    headers: { 'Content-Type': 'application/json' },
    keepalive: true,
    method: 'POST',
  }).catch(() => {
    // swallow — fire-and-forget
  });
}
